import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  PropertyFilter,
  Table,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';

import { listCatalogs } from 'src/api/catalog';
import { DATA_LOAD_LIMIT, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { createCatalogDetailLink } from 'src/routes';
import { EmptyState } from 'src/commons/EmptyState';
import { getHCResourcePagePropertyFilter, isSearchEnabled } from 'src/commons/common';
import { ownerLinkItem } from 'src/components/workspaces/common/common';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface BrowseCatalogsTableProps extends CommonPageProps {
  title: string;
  loading?: boolean;
}

const BrowseCatalogsTable = (props: BrowseCatalogsTableProps) => {
  const [allItems, setItems] = useState([]);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);

  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [nextToken, setNextToken] = useState(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [paginationDisabled, setPaginationDisabled] = useState(false);

  const columnDefinitions = [
    {
      id: 'catalogName',
      header: 'Catalog name',
      cell: (item) => (
        <Link
          href={createCatalogDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            item?.clusterId,
            item?.redshiftWorkgroupName,
            item?.bucketName,
            item?.region,
          )}
        >
          {item.catalogName}
        </Link>
      ),
      minWidth: 200,
      sortingField: 'catalogName',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item) => item.description,
      minWidth: 200,
    },
    {
      id: 'catalogId',
      header: 'Catalog ID',
      cell: (item) => item.catalogId,
      minWidth: 100,
    },
    {
      id: 'clusterName',
      header: 'Cluster identifier',
      cell: (item) => item.clusterIdentifier,
      sortingField: 'clusterName',
      minWidth: 100,
    },
    {
      id: 'owner',
      header: 'Owner',
      cell: (item) => ownerLinkItem(item.owner, props.workspaceNameMap),
      minWidth: 200,
      sortingField: 'owner',
    },
  ];

  // simplifies the catalog objects in order to make the rest of the code a bit cleaner
  const flatten = (items) => {
    return items.map((item) => ({
      catalogName: item.Name,
      catalogId: item.CatalogId,
      clusterId: item.ClusterIdentifier,
      redshiftWorkgroupName: item.RedshiftWorkgroupName,
      bucketName: item.BucketName,
      region: item.Region,
      description: item.Description,
      owner: item.Owner,
      clusterIdentifier: item?.ClusterIdentifier,
      dataSourceId: item?.DataSourceId,
    }));
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    fetchAllData();
  }, []);

  // Fetch databases and then their catalogs
  const fetchAllData = async () => {
    await fetchCatalogs();
  };

  const fetchCatalogs = async () => {
    setCatalogsLoading(true);
    let request = { Limit: DATA_LOAD_LIMIT, IncludeTotalCount: true };
    const catalogs = await listCatalogs(request);
    setTotalCount(catalogs.TotalCount);
    setNextToken(catalogs.NextToken);
    if (catalogs.NextToken == null) {
      setAllDataLoaded(true);
    }
    setItems(catalogs.CatalogInfoList);
    setCatalogsLoading(false);
  };

  const loadMoreData = async () => {
    setPaginationDisabled(true);
    setCatalogsLoading(true);
    let request = {
      NextToken: nextToken,
      Limit: DATA_LOAD_LIMIT,
    };
    let catalogs = await listCatalogs(request);
    setNextToken(catalogs.NextToken);
    let currentItems = allItems;
    currentItems.push(...catalogs.CatalogInfoList);
    setItems(currentItems);
    if (catalogs.NextToken == null) {
      setAllDataLoaded(true);
    }
    setCatalogsLoading(false);
    setPaginationDisabled(false);
    return catalogs.CatalogInfoList.length;
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    flatten(allItems),
    {
      filtering: {
        noMatch: {}, // filtering is disabled
        empty: <EmptyState title='No matches' subtitle='No catalogs were found.' />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [
          {
            propertyLabel: 'Catalog name',
            key: 'catalogName',
            groupValuesLabel: 'Catalog names',
          },
          {
            propertyLabel: 'Owner',
            key: 'owner',
            groupValuesLabel: 'Owners',
          },
        ],
      },
    },
  );

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex == paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreData();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading catalogs...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={
          <Header variant={'h2'} counter={`(${totalCount})`}>
            {props.title}
          </Header>
        }
        loading={props.loading || catalogsLoading}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={isSearchEnabled()}
            i18nStrings={getHCResourcePagePropertyFilter()}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={paginationLabels}
            onChange={(e) => handlePageChange(e)}
            openEnd={!allDataLoaded}
            disabled={paginationDisabled}
          />
        }
      />
    </>
  );
};

export default BrowseCatalogsTable;
